/**
 * DimensionSplit — stacked bar per periode met de dimensie-splitsing van een
 * metric (m²: particulier vs wederverkoper; uren: per afdeling). Gebruikt
 * dezelfde bron-reeks als de trendgrafiek.
 */

import { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  formatMetricValue,
  formatPointLabel,
  type Grain,
  type Series,
} from '../../stores/cijfers-store';

const DIM_COLORS = [
  'var(--brand-accent, #4f46e5)',
  '#94a3b8',
  '#34d399',
  '#fbbf24',
  '#f87171',
];

interface DimensionSplitProps {
  series: Series;
  grain: Grain;
  height?: number;
}

export function DimensionSplit({ series, grain, height = 240 }: DimensionSplitProps) {
  const { rows, dims } = useMemo(() => {
    const dimSet = new Set<string>();
    const byPeriod = new Map<string, Record<string, number | string>>();
    for (const p of series.points) {
      if (p.value === null || p.dimension === '') continue;
      dimSet.add(p.dimension);
      const row = byPeriod.get(p.period) ?? { period: p.period, label: formatPointLabel(p.period, grain) };
      row[p.dimension] = ((row[p.dimension] as number) ?? 0) + p.value;
      byPeriod.set(p.period, row);
    }
    return {
      rows: [...byPeriod.values()].sort((a, b) => String(a.period).localeCompare(String(b.period))),
      dims: [...dimSet].sort(),
    };
  }, [series, grain]);

  if (rows.length === 0 || dims.length === 0) return null;

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={rows} margin={{ top: 8, right: 8, bottom: 4, left: 8 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
        <XAxis
          dataKey="label"
          tick={{ fill: '#6b7280', fontSize: 11 }}
          tickLine={false}
          axisLine={{ stroke: '#e5e7eb' }}
          minTickGap={24}
        />
        <YAxis tick={{ fill: '#6b7280', fontSize: 11 }} tickLine={false} axisLine={false} width={56} />
        <Tooltip
          contentStyle={{
            backgroundColor: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            fontSize: 12,
          }}
          formatter={(value: number, name: string) => [formatMetricValue(value, series.format), name]}
        />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        {dims.map((dim, i) => (
          <Bar
            key={dim}
            dataKey={dim}
            stackId="dims"
            fill={DIM_COLORS[i % DIM_COLORS.length]}
            isAnimationActive={false}
            maxBarSize={28}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}
